/**
 * 🔥 VIRAL TAB - Architecture Mozart/Jobs/Tesla
 * 
 * L'onglet des fonctionnalités virales : Hot or Not, Secret Crush, Partage.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Flame, Heart, Share2, Users, Trophy, Eye, Lock } from 'lucide-react';
import { Button } from '../../ui/button';
import { HotOrNot, SecretCrush, ShareCard, InviteFriends } from '../features/viral';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ViralTab = ({ customerId, isDarkMode, profile }) => {
  const [activeSection, setActiveSection] = useState(null); // 'hot' | 'crush' | 'share' | 'invite'
  const [hotOrNotPair, setHotOrNotPair] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [crushes, setCrushes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Charger le leaderboard
  const loadLeaderboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/viral/hot-or-not/leaderboard`);
      const data = await response.json();
      if (data.success) {
        setLeaderboard(data.leaderboard || []);
      }
    } catch (err) {
      console.error('Error loading leaderboard:', err);
    }
  };

  // Charger une paire Hot or Not
  const loadHotOrNotPair = async () => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/viral/hot-or-not/pair/${customerId}`);
      const data = await response.json();
      if (data.success && data.pair) {
        setHotOrNotPair(data.pair);
      } else {
        setHotOrNotPair(null);
      }
    } catch (err) {
      console.error('Error loading pair:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Voter
  const handleVote = async (winnerId) => {
    if (!hotOrNotPair) return;
    
    try {
      const loserId = hotOrNotPair.find(p => p.id !== winnerId)?.id;
      await fetch(`${API_URL}/api/viral/hot-or-not/vote?voter_id=${customerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ winnerId, loserId })
      });
      toast.success('🔥 Vote enregistré !');
      loadHotOrNotPair();
    } catch (err) {
      console.error('Error voting:', err);
    }
  };

  // Charger mes crushes
  const loadCrushes = async () => {
    if (!customerId) return;
    try {
      const response = await fetch(`${API_URL}/api/viral/crush/my-crushes/${customerId}`);
      const data = await response.json();
      if (data.success) {
        setCrushes(data.crushes || []);
      }
    } catch (err) {
      console.error('Error loading crushes:', err);
    }
  };

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const sections = [
    {
      id: 'hot',
      title: 'Hot or Not',
      description: 'Vote pour tes préférés !',
      emoji: '🔥',
      icon: Flame,
      color: 'from-orange-500 to-red-500',
      onClick: () => {
        setActiveSection('hot');
        loadHotOrNotPair();
      }
    },
    {
      id: 'crush',
      title: 'Crush Secret',
      description: 'Envoie un crush anonyme',
      emoji: '💘',
      icon: Heart,
      color: 'from-pink-500 to-rose-500',
      onClick: () => {
        setActiveSection('crush');
        loadCrushes();
      }
    },
    {
      id: 'share',
      title: 'Partager',
      description: 'Montre ton profil au monde',
      emoji: '📱',
      icon: Share2,
      color: 'from-blue-500 to-cyan-500',
      onClick: () => setActiveSection('share')
    },
    {
      id: 'invite',
      title: 'Inviter',
      description: 'Gagne des couronnes !',
      emoji: '👑',
      icon: Users,
      color: 'from-yellow-500 to-orange-500',
      onClick: () => setActiveSection('invite')
    }
  ];

  // Vue principale
  if (!activeSection) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            🔥 Zone Virale
          </h2>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Fais le buzz et gagne des couronnes !
          </p>
        </div>

        {/* Section Cards */}
        <div className="grid grid-cols-2 gap-3">
          {sections.map((section, index) => (
            <motion.button
              key={section.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              onClick={section.onClick}
              className={`p-4 rounded-2xl text-left transition-all hover:scale-[1.02] ${
                isDarkMode 
                  ? 'bg-gray-800/70 border border-gray-700 hover:border-purple-500/50' 
                  : 'bg-white border border-gray-200 hover:border-purple-300 shadow-sm'
              }`}
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${section.color} flex items-center justify-center text-white text-2xl mb-3`}>
                {section.emoji}
              </div>
              <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {section.title}
              </h3>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {section.description}
              </p>
            </motion.button>
          ))}
        </div>

        {/* Mini Leaderboard */}
        {leaderboard.length > 0 && (
          <div className={`p-4 rounded-2xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-5 h-5 text-yellow-500" />
              <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Top 3 Hot
              </h3>
            </div>
            <div className="space-y-2">
              {leaderboard.slice(0, 3).map((user, index) => (
                <div key={user.id} className="flex items-center gap-3">
                  <span className="text-lg">
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                  </span>
                  <span className={`flex-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {user.name}
                  </span>
                  <span className={`font-bold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                    {user.score}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Hot or Not
  if (activeSection === 'hot') {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => setActiveSection(null)} className="mb-4">
          ← Retour
        </Button>
        
        <div className="text-center mb-6">
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            🔥 Hot or Not
          </h2>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Qui préfères-tu ?
          </p>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin text-4xl">🔥</div>
          </div>
        ) : hotOrNotPair ? (
          <div className="flex gap-4">
            {hotOrNotPair.map(person => (
              <motion.button
                key={person.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleVote(person.id)}
                className={`flex-1 p-4 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
              >
                <div className="w-full aspect-square rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-5xl mb-3">
                  {person.avatar || '😊'}
                </div>
                <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {person.name}
                </h3>
              </motion.button>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Plus de profils pour le moment</p>
            <Button onClick={loadHotOrNotPair} className="mt-4">
              🔄 Actualiser
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Crush Secret
  if (activeSection === 'crush') {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => setActiveSection(null)} className="mb-4">
          ← Retour
        </Button>
        <SecretCrush 
          customerId={customerId} 
          isDarkMode={isDarkMode}
          crushes={crushes}
          onCrushSent={loadCrushes}
        />
      </div>
    );
  }

  // Share
  if (activeSection === 'share') {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => setActiveSection(null)} className="mb-4">
          ← Retour
        </Button>
        <ShareCard profile={profile} isDarkMode={isDarkMode} />
      </div>
    );
  }

  // Invite
  if (activeSection === 'invite') {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => setActiveSection(null)} className="mb-4">
          ← Retour
        </Button>
        <InviteFriends customerId={customerId} isDarkMode={isDarkMode} />
      </div>
    );
  }

  return null;
};

export default ViralTab;
